/**
 * DeleteAdminAccountConfirmModal Component
 * Three-stage confirmation flow for permanently deleting an admin account.
 *
 * Stage 1 — Initial warning: "Are you absolutely sure?"
 * Stage 2 — What gets erased: explicit checklist the admin must read and acknowledge
 * Stage 3 — Type-to-confirm: must type their email address before the button unlocks
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const overlayStyle = {
  position: 'fixed',
  top: 0, right: 0, bottom: 0, left: 0,
  background: 'rgba(0, 0, 0, 0.85)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 2147483647,
  padding: '24px',
  overflowY: 'auto',
  boxSizing: 'border-box',
  backdropFilter: 'blur(2px)',
};

const cardStyle = {
  width: '100%',
  maxWidth: '640px',
  background: '#0f1f18',
  border: '1px solid #7f1d1d',
  borderRadius: '16px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  margin: 'auto',
  boxSizing: 'border-box',
};

const stepIndicatorRow = {
  display: 'flex',
  gap: '8px',
  marginBottom: '28px',
  alignItems: 'center',
};

const stepDot = (active, done) => ({
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: '800',
  flexShrink: 0,
  background: done ? '#14532d' : active ? '#b91c1c' : '#1a2e25',
  color: done ? '#4ade80' : active ? '#ffffff' : '#4b5563',
  border: done ? '1px solid #4ade80' : active ? '1px solid #ef4444' : '1px solid #2d4f3c',
  transition: 'all 0.2s',
});

const stepLine = {
  flex: 1,
  height: '2px',
  background: '#2d4f3c',
  borderRadius: '2px',
};

const titleStyle = {
  margin: '0 0 12px 0',
  color: '#ffffff',
  fontSize: '22px',
  fontWeight: '800',
};

const textStyle = {
  margin: '0 0 16px 0',
  color: '#9ca3af',
  fontSize: '15px',
  lineHeight: 1.6,
};

const checklistItem = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '10px 14px',
  background: '#152318',
  border: '1px solid #2d4f3c',
  borderRadius: '8px',
  marginBottom: '8px',
};

const checkIcon = {
  color: '#f87171',
  fontSize: '16px',
  fontWeight: '800',
  flexShrink: 0,
  marginTop: '1px',
};

const checkText = {
  color: '#d1fae5',
  fontSize: '14px',
  lineHeight: 1.5,
  margin: 0,
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #4b5563',
  borderRadius: '8px',
  fontSize: '15px',
  marginBottom: '18px',
  boxSizing: 'border-box',
  background: '#152318',
  color: '#ffffff',
  outline: 'none',
};

const actionRow = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
  marginTop: '8px',
};

const btnBase = {
  borderRadius: '8px',
  border: '1px solid transparent',
  padding: '12px 18px',
  fontSize: '15px',
  fontWeight: '700',
  cursor: 'pointer',
  flex: 1,
};

const cancelBtn = {
  ...btnBase,
  background: 'transparent',
  borderColor: '#2d4f3c',
  color: '#9ca3af',
};

const continueBtn = {
  ...btnBase,
  background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
  color: '#ffffff',
  boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
};

const disabledStyle = {
  opacity: 0.45,
  cursor: 'not-allowed',
};

const errorStyle = {
  color: '#f87171',
  fontSize: '13px',
  margin: '-8px 0 14px 0',
};

const WHAT_GETS_ERASED = [
  'Your admin account entry — you will lose all admin access immediately',
  'Your ability to sign back in with this email address',
  'All audit log history associated with your account',
  'This action is permanent and cannot be reversed by anyone',
];

const DeleteAdminAccountConfirmModal = ({
  isOpen,
  userEmail,
  isSubmitting,
  error,
  onCancel,
  onConfirm,
}) => {
  const [stage, setStage] = useState(1);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  if (!isOpen) return null;

  const emailMatches =
    confirmationText.trim().toLowerCase() === (userEmail || '').trim().toLowerCase();

  const handleCancel = () => {
    // Reset internal state whenever the modal is dismissed
    setStage(1);
    setAcknowledged(false);
    setConfirmationText('');
    onCancel();
  };

  const handleBack = () => {
    setStage((s) => Math.max(1, s - 1));
  };

  return createPortal(
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-admin-modal-title"
    >
      <div style={cardStyle}>

        {/* Step indicators */}
        <div style={stepIndicatorRow}>
          {[1, 2, 3].map((n, i) => (
            <React.Fragment key={n}>
              <div style={stepDot(stage === n, stage > n)}>
                {stage > n ? '✓' : n}
              </div>
              {i < 2 && <div style={stepLine} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Stage 1: Initial warning ── */}
        {stage === 1 && (
          <>
            <h3 id="delete-admin-modal-title" style={titleStyle}>
              Delete your admin account?
            </h3>
            <p style={textStyle}>
              You are about to permanently delete your admin account. This will remove your
              admin access and cannot be undone.
            </p>
            <p style={textStyle}>
              Are you absolutely sure you want to continue?
            </p>
            <div style={actionRow}>
              <button type="button" style={cancelBtn} onClick={handleCancel} disabled={isSubmitting}>
                No, keep my account
              </button>
              <button
                type="button"
                style={continueBtn}
                onClick={() => setStage(2)}
                disabled={isSubmitting}
              >
                Yes, continue →
              </button>
            </div>
          </>
        )}

        {/* ── Stage 2: What gets erased checklist ── */}
        {stage === 2 && (
          <>
            <h3 id="delete-admin-modal-title" style={titleStyle}>
              Understand what will be erased
            </h3>
            <p style={textStyle}>
              Before continuing, confirm you understand exactly what deleting your account means:
            </p>
            {WHAT_GETS_ERASED.map((item) => (
              <div key={item} style={checklistItem}>
                <span style={checkIcon}>✕</span>
                <p style={checkText}>{item}</p>
              </div>
            ))}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '16px',
                marginBottom: '8px',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={isSubmitting}
                style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer' }}
              />
              <span style={{ color: '#d1fae5', fontSize: '14px', fontWeight: '600' }}>
                I understand and accept these consequences
              </span>
            </label>
            <div style={actionRow}>
              <button type="button" style={cancelBtn} onClick={handleBack} disabled={isSubmitting}>
                ← Go back
              </button>
              <button
                type="button"
                style={!acknowledged || isSubmitting ? { ...continueBtn, ...disabledStyle } : continueBtn}
                onClick={() => setStage(3)}
                disabled={!acknowledged || isSubmitting}
              >
                I understand, continue →
              </button>
            </div>
          </>
        )}

        {/* ── Stage 3: Type email to confirm ── */}
        {stage === 3 && (
          <>
            <h3 id="delete-admin-modal-title" style={titleStyle}>
              Final confirmation
            </h3>
            <p style={textStyle}>
              This is your last chance to change your mind.
            </p>
            <p style={textStyle}>
              Type your email address{' '}
              <strong style={{ color: '#f87171' }}>{userEmail}</strong>{' '}
              below to permanently delete your account.
            </p>
            <input
              type="text"
              style={inputStyle}
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type your email to confirm"
              disabled={isSubmitting}
              autoFocus
            />
            {error && <p style={errorStyle}>{error}</p>}
            <div style={actionRow}>
              <button type="button" style={cancelBtn} onClick={handleBack} disabled={isSubmitting}>
                ← Go back
              </button>
              <button
                type="button"
                style={!emailMatches || isSubmitting ? { ...continueBtn, ...disabledStyle } : continueBtn}
                onClick={onConfirm}
                disabled={!emailMatches || isSubmitting}
              >
                {isSubmitting ? 'Deleting account...' : 'Permanently delete my account'}
              </button>
            </div>
          </>
        )}

      </div>
    </div>,
    document.body
  );
};

export default DeleteAdminAccountConfirmModal;
